import { defineStore } from 'pinia'
import Api from '@/api/Api'

const replaceById = (items, item) => items.map((existingItem) => (
  existingItem.id === item.id ? item : existingItem
));

const prependById = (items, item) => [
  item,
  ...items.filter((existingItem) => existingItem.id !== item.id),
];

const getEndpointDeliveryKey = (integrationId, endpointId) => `${integrationId}:${endpointId}`;

export const useWebhookIntegrationStore = defineStore('webhookIntegration', {
  state: () => ({
    integrations: [],
    integrationTypes: [],
    endpointsByIntegrationId: {},
    integrationDeliveriesByIntegrationId: {},
    endpointDeliveriesByKey: {},
    endpointDeliverySummariesByKey: {},
    deliveryDetailsById: {},
    loading: false,
  }),
  getters: {
    getIntegrations: (state) => state.integrations,
    getIntegrationTypes: (state) => state.integrationTypes,
    getEndpoints: (state) => (integrationId) => state.endpointsByIntegrationId[integrationId] || [],
    getIntegrationDeliveries: (state) => (integrationId) => state.integrationDeliveriesByIntegrationId[integrationId] || [],
    getEndpointDeliveries: (state) => (integrationId, endpointId) => {
      const key = getEndpointDeliveryKey(integrationId, endpointId);
      return state.endpointDeliveriesByKey[key] || [];
    },
    getEndpointDeliverySummary: (state) => (integrationId, endpointId) => {
      const key = getEndpointDeliveryKey(integrationId, endpointId);
      return state.endpointDeliverySummariesByKey[key] || null;
    },
    getDeliveryDetail: (state) => (deliveryId) => state.deliveryDetailsById[deliveryId] || null,
    isLoading: (state) => state.loading,
  },
  actions: {
    async fetchIntegrations(payload) {
      this.loading = true;
      try {
        const data = await Api.webhookIntegration.getIntegrations(payload);
        this.integrations = data;
        return data;
      } finally {
        this.loading = false;
      }
    },

    async fetchIntegrationTypes(payload) {
      const data = await Api.webhookIntegration.getIntegrationTypes(payload);
      this.integrationTypes = data;
      return data;
    },

    async createIntegration(payload) {
      const integration = await Api.webhookIntegration.createIntegration(payload);
      this.integrations = [integration, ...this.integrations];
      return integration;
    },

    async updateIntegration(payload) {
      let integration = await Api.webhookIntegration.updateIntegration(payload);
      if (payload.state && payload.state !== integration.state) {
        integration = await Api.webhookIntegration.updateIntegrationState(payload);
      }
      this.integrations = replaceById(this.integrations, integration);
      return integration;
    },

    async updateIntegrationState(payload) {
      const integration = await Api.webhookIntegration.updateIntegrationState(payload);
      this.integrations = replaceById(this.integrations, integration);
      return integration;
    },

    async deleteIntegration(payload) {
      await Api.webhookIntegration.deleteIntegration(payload);
      this.integrations = this.integrations.filter((integration) => integration.id !== payload.integrationId);
    },

    async fetchEndpoints(payload) {
      const endpoints = await Api.webhookIntegration.getEndpoints(payload);
      this.endpointsByIntegrationId = {
        ...this.endpointsByIntegrationId,
        [payload.integrationId]: endpoints,
      };
      return endpoints;
    },

    async fetchIntegrationDeliveries(payload) {
      const deliveries = await Api.webhookIntegration.getIntegrationDeliveries(payload);
      this.integrationDeliveriesByIntegrationId = {
        ...this.integrationDeliveriesByIntegrationId,
        [payload.integrationId]: deliveries,
      };
      return deliveries;
    },

    async fetchEndpointDeliverySummary(payload) {
      const summary = await Api.webhookIntegration.getEndpointDeliverySummary(payload);
      const key = getEndpointDeliveryKey(payload.integrationId, payload.endpointId);
      this.endpointDeliverySummariesByKey = {
        ...this.endpointDeliverySummariesByKey,
        [key]: summary,
      };
      return summary;
    },

    async fetchEndpointDeliveries(payload) {
      const deliveries = await Api.webhookIntegration.getEndpointDeliveries(payload);
      const key = getEndpointDeliveryKey(payload.integrationId, payload.endpointId);
      this.endpointDeliveriesByKey = {
        ...this.endpointDeliveriesByKey,
        [key]: deliveries,
      };
      return deliveries;
    },

    async fetchEndpointDelivery(payload) {
      const delivery = await Api.webhookIntegration.getEndpointDelivery(payload);
      this.deliveryDetailsById = {
        ...this.deliveryDetailsById,
        [delivery.id]: delivery,
      };
      return delivery;
    },

    async createEndpoint(payload) {
      const endpoint = await Api.webhookIntegration.createEndpoint(payload);
      const endpoints = this.getEndpoints(payload.integrationId);
      this.endpointsByIntegrationId = {
        ...this.endpointsByIntegrationId,
        [payload.integrationId]: [endpoint, ...endpoints],
      };
      return endpoint;
    },

    async updateEndpoint(payload) {
      let endpoint = await Api.webhookIntegration.updateEndpoint(payload);
      if (payload.state && payload.state !== endpoint.state) {
        endpoint = await Api.webhookIntegration.updateEndpointState(payload);
      }
      this.replaceEndpoint(payload.integrationId, endpoint);
      return endpoint;
    },

    async updateEndpointState(payload) {
      const endpoint = await Api.webhookIntegration.updateEndpointState(payload);
      this.replaceEndpoint(payload.integrationId, endpoint);
      return endpoint;
    },

    async deleteEndpoint(payload) {
      await Api.webhookIntegration.deleteEndpoint(payload);
      const endpoints = this.getEndpoints(payload.integrationId);
      this.endpointsByIntegrationId = {
        ...this.endpointsByIntegrationId,
        [payload.integrationId]: endpoints.filter((endpoint) => endpoint.id !== payload.endpointId),
      };
    },

    async rotateSigningSecret(payload) {
      const endpoint = await Api.webhookIntegration.rotateSigningSecret(payload);
      this.replaceEndpoint(payload.integrationId, endpoint);
      return endpoint;
    },

    async sendTest(payload) {
      return Api.webhookIntegration.sendTest(payload);
    },

    async redeliverDelivery(payload) {
      const delivery = await Api.webhookIntegration.redeliver(payload);
      const key = getEndpointDeliveryKey(payload.integrationId, payload.endpointId);
      this.integrationDeliveriesByIntegrationId = {
        ...this.integrationDeliveriesByIntegrationId,
        [payload.integrationId]: prependById(this.getIntegrationDeliveries(payload.integrationId), delivery),
      };
      this.endpointDeliveriesByKey = {
        ...this.endpointDeliveriesByKey,
        [key]: prependById(this.getEndpointDeliveries(payload.integrationId, payload.endpointId), delivery),
      };
      this.deliveryDetailsById = {
        ...this.deliveryDetailsById,
        [delivery.id]: delivery,
      };
      return delivery;
    },

    replaceEndpoint(integrationId, endpoint) {
      const endpoints = this.getEndpoints(integrationId);
      this.endpointsByIntegrationId = {
        ...this.endpointsByIntegrationId,
        [integrationId]: replaceById(endpoints, endpoint),
      };
    },
  },
});
